<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from '../i18n'
import { useSiteSettings } from '../composables/useSiteSettings'

export default {
  setup() {
    const { t, locale, setLocale } = useI18n()
    const router = useRouter()
    const route = useRoute()
    const mobileOpen = ref(false)
    const scrolled = ref(false)
    const { loadSettings, getSetting } = useSiteSettings()

    const navLinks = computed(() => [
      { name: t('nav.home'), path: '/' },
      { name: t('nav.products'), path: '/products' },
      { name: t('nav.cases'), path: '/cases' },
      { name: t('nav.news'), path: '/news' },
      { name: t('nav.about'), path: '/about' },
      { name: t('nav.contact'), path: '/contact' },
    ])

    const languages = [
      { code: 'en', label: 'English', flagClass: 'flag-en' },
      { code: 'es', label: 'Español', flagClass: 'flag-es' },
      { code: 'zh', label: '中文', flagClass: 'flag-zh' },
    ]

    function onScroll() {
      scrolled.value = window.scrollY > 30
    }

    onMounted(() => window.addEventListener('scroll', onScroll))
    onUnmounted(() => window.removeEventListener('scroll', onScroll))

    function closeMobile() {
      mobileOpen.value = false
    }

    function changeLang(code) {
      setLocale(code)
      loadSettings(code)
    }

    return { t, locale, setLocale, navLinks, mobileOpen, scrolled, closeMobile, route, languages, changeLang, getSetting }
  },
}
</script>

<template>
  <nav class="navbar" :class="{ scrolled }">
    <div class="navbar-inner">
      <!-- Logo far left -->
      <router-link to="/" class="navbar-logo" @click="closeMobile">
        <img v-if="getSetting('site.logo')" :src="getSetting('site.logo')" alt="Logo" class="navbar-logo-image" />
        <span v-else class="navbar-logo-text">Coronet</span>
      </router-link>

      <!-- Menu center -->
      <div class="navbar-links">
        <router-link
          v-for="link in navLinks"
          :key="link.path"
          :to="link.path"
        >
          {{ link.name }}
        </router-link>
      </div>

      <!-- Language selector + mobile toggle -->
      <div class="navbar-actions">
        <div class="lang-flags">
          <button
            v-for="lang in languages"
            :key="lang.code"
            class="flag-btn"
            :class="[lang.flagClass, { active: locale === lang.code }]"
            @click="changeLang(lang.code)"
            :title="lang.label"
          >
          </button>
        </div>
        <button class="menu-toggle" @click="mobileOpen = !mobileOpen">
          <svg v-if="!mobileOpen" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><line x1="3" y1="7" x2="21" y2="7"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="17" x2="21" y2="17"/></svg>
          <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
    </div>

    <Transition name="slide">
      <div v-if="mobileOpen" class="mobile-menu">
        <router-link
          v-for="link in navLinks"
          :key="link.path"
          :to="link.path"
          @click="closeMobile"
        >
          {{ link.name }}
        </router-link>
        <div style="display:flex;gap:8px;padding:16px 0;border-top:1px solid var(--border);">
          <button
            v-for="lang in languages"
            :key="lang.code"
            class="tab-btn"
            :class="{ active: locale === lang.code }"
            @click="changeLang(lang.code)"
          >
            <span style="font-size:18px;">{{ lang.flag }}</span> {{ lang.label }}
          </button>
        </div>
      </div>
    </Transition>
  </nav>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active { transition: all 0.3s ease; }
.slide-enter-from,
.slide-leave-to { opacity: 0; transform: translateY(-12px); }

.lang-flags {
  display: flex;
  align-items: center;
  gap: 12px;
}
.flag-btn {
  width: 28px;
  height: 20px;
  border: 2px solid transparent;
  border-radius: 3px;
  cursor: pointer;
  padding: 0;
  opacity: 0.5;
  transition: all 0.2s;
  overflow: hidden;
  position: relative;
}
.flag-btn:hover {
  opacity: 0.8;
  transform: scale(1.15);
}
.flag-btn.active {
  opacity: 1;
  border-color: rgba(255,255,255,0.6);
}

/* UK Flag */
.flag-en {
  background: #012169;
}
.flag-en::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(to bottom, transparent 40%, #fff 40%, #fff 60%, transparent 60%),
    linear-gradient(to right, transparent 45%, #fff 45%, #fff 55%, transparent 55%);
}
.flag-en::after {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(to bottom, transparent 42%, #C8102E 42%, #C8102E 58%, transparent 58%),
    linear-gradient(to right, transparent 47%, #C8102E 47%, #C8102E 53%, transparent 53%);
}

/* Spain Flag */
.flag-es {
  background: linear-gradient(to bottom, #AA151B 25%, #F1BF00 25%, #F1BF00 75%, #AA151B 75%);
}

/* China Flag */
.flag-zh {
  background: #DE2910;
}
.flag-zh::before {
  content: '★';
  position: absolute;
  top: 2px;
  left: 4px;
  font-size: 10px;
  color: #FFDE00;
  line-height: 1;
}
</style>
